import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { FileSpreadsheet, Camera, Sparkles, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WelcomeFirstRunModal
 *
 * Full-screen first-login modal that nudges new users into the
 * Spreadsheet Uploader (where the platform's real value — MFI,
 * harvest correction, growth trajectory — actually shines) instead
 * of the lighter-weight Snapshot sandbox.
 *
 * Two paths:
 *   1. "See the real PFD" (recommended) — routes to the Spreadsheet
 *      Comparison page with a curated Seabream example auto-loaded.
 *   2. "Just play around" — routes to Snapshot.
 *
 * The user's choice is persisted on the user record via
 * POST /api/auth/welcome-path so the modal never auto-opens again.
 *
 * Props:
 *   open          bool                — show / hide
 *   onPathChosen  (path: string) => void
 *                                       Called AFTER the backend has
 *                                       persisted the choice so the
 *                                       parent can navigate.
 */
export default function WelcomeFirstRunModal({ open, onPathChosen }) {
  const { token, refreshUser } = useAuth();
  const [submitting, setSubmitting] = useState(null); // 'spreadsheet' | 'snapshot' | null
  // "Don't show me this again" — when ticked, the user's next login will
  // skip this modal permanently.
  const [dontShowAgain, setDontShowAgain] = useState(false);

  if (!open) return null;

  const choose = async (path) => {
    if (submitting) return;
    setSubmitting(path);
    try {
      await axios.post(
        `${API}/auth/welcome-path`,
        { path, dont_show_again: dontShowAgain },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch {
      // Non-blocking — still navigate even if the persist call fails.
    }
    try { await refreshUser?.(); } catch { /* ignore */ }
    setSubmitting(null);
    onPathChosen?.(path);
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="welcome-first-run-modal"
    >
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl my-8 border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="p-7 pb-4 text-center border-b border-slate-100 dark:border-slate-800">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 mb-3 shadow-md">
            <Sparkles className="w-7 h-7 text-white" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100">
            Welcome to Precision Fish Detection
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 max-w-xl mx-auto">
            How would you like to begin? Most users get the strongest first
            impression from a full cycle analysis &mdash; but it&apos;s your call.
          </p>
        </div>

        {/* Two paths */}
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          {/* Recommended: Spreadsheet uploader with demo */}
          <button
            type="button"
            onClick={() => choose('spreadsheet')}
            disabled={submitting !== null}
            data-testid="welcome-path-spreadsheet-btn"
            className="group relative text-left rounded-xl border-2 border-cyan-500 bg-gradient-to-br from-cyan-50 via-white to-teal-50 dark:from-cyan-900/30 dark:via-slate-900 dark:to-teal-900/30 hover:shadow-xl hover:-translate-y-0.5 transition-all p-5 disabled:opacity-60 disabled:cursor-wait focus:outline-none focus:ring-4 focus:ring-cyan-200"
          >
            <span className="absolute -top-2.5 left-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-cyan-600 text-white shadow">
              Recommended
            </span>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-cyan-600 flex items-center justify-center shadow">
                <FileSpreadsheet className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                  See the real PFD
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-snug">
                  Open the Spreadsheet Uploader with a curated Seabream
                  cycle pre-loaded. See Missing Fish Intelligence,
                  Harvest Correction signals and the full growth
                  trajectory in under 30&nbsp;seconds.
                </p>
                <ul className="mt-3 space-y-1 text-[11px] text-slate-700 dark:text-slate-300">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    Full cycle analysis &amp; AI Wingman narrative
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Missing-fish &amp; harvest-jump signals on the chart
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                    Guardrails OFF so you see every signal
                  </li>
                </ul>
                <span className="mt-3 inline-flex items-center text-xs font-semibold text-cyan-700 dark:text-cyan-300 group-hover:translate-x-0.5 transition-transform">
                  {submitting === 'spreadsheet' ? 'Loading…' : 'Start full demo'}
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </span>
              </div>
            </div>
          </button>

          {/* Alternative: Snapshot */}
          <button
            type="button"
            onClick={() => choose('snapshot')}
            disabled={submitting !== null}
            data-testid="welcome-path-snapshot-btn"
            className="group text-left rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-400 hover:shadow-md transition-all p-5 disabled:opacity-60 disabled:cursor-wait focus:outline-none focus:ring-4 focus:ring-slate-200"
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-11 h-11 rounded-lg bg-slate-500 flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                  Just play around
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-snug">
                  Open the Snapshot sandbox &mdash; enter a few numbers and
                  get a single point-in-time FCR estimate. Quick, but
                  doesn&apos;t show what PFD does over a full cycle.
                </p>
                <span className="mt-7 inline-flex items-center text-xs font-medium text-slate-600 dark:text-slate-300 group-hover:translate-x-0.5 transition-transform">
                  {submitting === 'snapshot' ? 'Loading…' : 'Open Snapshot'}
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </span>
              </div>
            </div>
          </button>
        </div>

        <div className="px-6 pb-5 -mt-1 space-y-3">
          <label
            className="flex items-center justify-center gap-2 text-xs text-slate-600 dark:text-slate-300 cursor-pointer select-none"
            data-testid="welcome-dont-show-again-label"
          >
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="accent-cyan-600 w-3.5 h-3.5"
              data-testid="welcome-dont-show-again"
            />
            Don&apos;t show me this again
          </label>
          <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center">
            You can switch between any module at any time &mdash; this is just
            where we&apos;ll start you.
          </p>
        </div>
      </div>
    </div>
  );
}
